/**
 * TenantUserAccessList Component
 * Displays a list of users with access to a specific tenant.
 *
 * Uses the DataGridCard template pattern which provides:
 * - DataGridToolbar with Left/Right sections
 * - Column sizing options
 * - Built-in loading, empty, and error states
 * - Pagination support
 *
 * Features:
 * - Lists all users who have been granted access
 * - Shows user details with avatar (name + email combined), organization, role
 * - Includes "Refresh" button on left, "Grant Access" button on right
 * - Integrates with GrantTenantAccessModal
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  TableColumnDefinition,
  createTableColumn,
  TableCellLayout,
  Text,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Avatar,
  tokens,
  Spinner,
} from '@fluentui/react-components';
import { Add20Regular, ArrowClockwise20Regular, Delete20Regular } from '@fluentui/react-icons';
import { Alert } from '../common/Alert';
import {
  DataGridCard,
  DataGridToolbar,
  ToolbarButton,
} from '../layouts';
import { GrantTenantAccessModal } from './GrantTenantAccessModal';
import { TenantApiService } from '../../services/tenantApi';
import type { TenantUserWithAccess } from '../../types/tenant';

export interface TenantUserAccessListProps {
  tenantId: string;
  onGrantSuccess?: () => void;
}

const formatDate = (dateString: string): string => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const columns: TableColumnDefinition<TenantUserWithAccess>[] = [
  createTableColumn<TenantUserWithAccess>({
    columnId: 'user',
    compare: (a, b) => a.user_name.localeCompare(b.user_name),
    renderHeaderCell: () => 'User',
    renderCell: (item) => (
      <TableCellLayout
        media={
          <Avatar
            name={item.user_name}
            size={32}
            color="colorful"
          />
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
          <Text weight="semibold" style={{ color: tokens.colorBrandBackgroundSelected, lineHeight: '1.2' }}>
            {item.user_name}
          </Text>
          <Text size={200} style={{ color: tokens.colorNeutralForeground3, lineHeight: '1.2' }}>
            {item.user_email}
          </Text>
        </div>
      </TableCellLayout>
    ),
  }),
  createTableColumn<TenantUserWithAccess>({
    columnId: 'organization_name',
    compare: (a, b) => a.organization_name.localeCompare(b.organization_name),
    renderHeaderCell: () => 'Organization',
    renderCell: (item) => (
      <TableCellLayout>
        <Text>{item.organization_name}</Text>
      </TableCellLayout>
    ),
  }),
  createTableColumn<TenantUserWithAccess>({
    columnId: 'matter_role',
    compare: (a, b) => (a.matter_role ?? '').localeCompare(b.matter_role ?? ''),
    renderHeaderCell: () => 'Matter Role',
    renderCell: (item) => (
      <TableCellLayout>
        <Text>{item.matter_role ?? '-'}</Text>
      </TableCellLayout>
    ),
  }),
  createTableColumn<TenantUserWithAccess>({
    columnId: 'granted_at',
    compare: (a, b) => a.granted_at.localeCompare(b.granted_at),
    renderHeaderCell: () => 'Granted At',
    renderCell: (item) => (
      <TableCellLayout>
        <Text>{formatDate(item.granted_at)}</Text>
      </TableCellLayout>
    ),
  }),
];

export const TenantUserAccessList: React.FC<TenantUserAccessListProps> = ({
  tenantId,
  onGrantSuccess,
}) => {
  const [users, setUsers] = useState<TenantUserWithAccess[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isGrantModalOpen, setIsGrantModalOpen] = useState(false);
  const [selectedUsers, setSelectedUsers] = useState<Set<string | number>>(new Set());
  const [isRevokeDialogOpen, setIsRevokeDialogOpen] = useState(false);
  const [isRevoking, setIsRevoking] = useState(false);
  const [revokeError, setRevokeError] = useState<string | null>(null);

  const fetchUsers = useCallback(async () => {
    setIsFetching(true);
    setError(null);
    try {
      const data = await TenantApiService.getTenantUserAccess(tenantId);
      setUsers(data);
    } catch {
      setError('Failed to load users with access');
    } finally {
      setIsLoading(false);
      setIsFetching(false);
    }
  }, [tenantId]);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleGrantSuccess = useCallback(() => {
    fetchUsers();
    onGrantSuccess?.();
  }, [fetchUsers, onGrantSuccess]);

  const handleRefresh = useCallback(() => {
    fetchUsers();
  }, [fetchUsers]);

  const handleRevokeClick = useCallback(() => {
    setRevokeError(null);
    setIsRevokeDialogOpen(true);
  }, []);

  const handleRevokeConfirm = useCallback(async () => {
    setIsRevoking(true);
    setRevokeError(null);
    try {
      // For each selected user, revoke their access to this tenant
      await Promise.all(
        Array.from(selectedUsers).map((userId) =>
          TenantApiService.revokeUserTenantAccess(tenantId, Number(userId))
        )
      );
      setSelectedUsers(new Set());
      setIsRevokeDialogOpen(false);
      fetchUsers();
    } catch (err) {
      setRevokeError(err instanceof Error ? err.message : 'Failed to revoke access');
    } finally {
      setIsRevoking(false);
    }
  }, [tenantId, selectedUsers, fetchUsers]);

  const handleRevokeDialogClose = useCallback(() => {
    if (!isRevoking) {
      setIsRevokeDialogOpen(false);
      setRevokeError(null);
    }
  }, [isRevoking]);

  // Render error state
  if (error && !isLoading) {
    return (
      <div>
        <Alert intent="error">{error}</Alert>
      </div>
    );
  }

  // Render toolbar
  const revokeButtonLabel = selectedUsers.size > 0
    ? `Revoke Access (${selectedUsers.size})`
    : 'Revoke Access';

  const renderToolbar = () => (
    <DataGridToolbar>
      <DataGridToolbar.Left>
        <ToolbarButton
          icon={<ArrowClockwise20Regular />}
          onClick={handleRefresh}
          loading={isFetching}
          loadingText="Refreshing..."
        >
          Refresh
        </ToolbarButton>
      </DataGridToolbar.Left>
      <DataGridToolbar.Right>
        <ToolbarButton
          icon={<Delete20Regular />}
          onClick={handleRevokeClick}
          disabled={selectedUsers.size === 0}
        >
          {revokeButtonLabel}
        </ToolbarButton>
        <ToolbarButton
          appearance="primary"
          icon={<Add20Regular />}
          onClick={() => setIsGrantModalOpen(true)}
        >
          Grant Access
        </ToolbarButton>
      </DataGridToolbar.Right>
    </DataGridToolbar>
  );

  return (
    <>
      <DataGridCard
        items={users}
        columns={columns}
        getRowId={(item) => String(item.user_id)}
        columnSizingOptions={{
          user: { minWidth: 200, defaultWidth: 280 },
          organization_name: { minWidth: 120, defaultWidth: 160 },
          matter_role: { minWidth: 100, defaultWidth: 120 },
          granted_at: { minWidth: 100, defaultWidth: 120 },
        }}
        toolbar={renderToolbar()}
        pagination={{
          showing: users.length,
          total: users.length,
        }}
        selectable={true}
        selectedIds={selectedUsers}
        onSelectionChange={setSelectedUsers}
        loading={isLoading}
        emptyMessage="No users have access to this tenant"
        testId="tenant-user-access-list"
      />

      <GrantTenantAccessModal
        isOpen={isGrantModalOpen}
        tenantId={tenantId}
        onClose={() => setIsGrantModalOpen(false)}
        onSuccess={handleGrantSuccess}
      />

      <Dialog open={isRevokeDialogOpen} onOpenChange={(_, data) => !data.open && handleRevokeDialogClose()}>
        <DialogSurface>
          <DialogBody>
            <DialogTitle>Revoke Access</DialogTitle>
            <DialogContent>
              {revokeError && (
                <Alert intent="error" style={{ marginBottom: '1rem' }}>
                  {revokeError}
                </Alert>
              )}
              <Text>
                Are you sure you want to revoke access for {selectedUsers.size} user
                {selectedUsers.size === 1 ? '' : 's'}? This action cannot be undone.
              </Text>
            </DialogContent>
            <DialogActions>
              <Button appearance="secondary" onClick={handleRevokeDialogClose} disabled={isRevoking}>
                Cancel
              </Button>
              <Button
                appearance="primary"
                onClick={handleRevokeConfirm}
                disabled={isRevoking}
                icon={isRevoking ? <Spinner size="tiny" /> : undefined}
              >
                {isRevoking ? 'Revoking...' : 'Revoke'}
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>
    </>
  );
};
